import { useCallback, useEffect, useRef } from 'react';
import type { FocusEvent, KeyboardEvent } from 'react';

/**
 * Roving tabindex for a navigation region (WAI-ARIA "roving tabindex" pattern).
 *
 * Turns a container full of focusable controls (e.g. the sidebar's section
 * headers + nav items) into a SINGLE Tab stop: only one item is tabbable at a
 * time, so pressing Tab moves into the region once and Tab again leaves it
 * straight to the next stop (the page content). Arrow keys (and Home/End) move
 * focus between the items inside the region. This removes the need to Tab
 * through every nav item before reaching the main content.
 *
 * Implementation notes:
 * - We manage `tabindex` directly on the DOM nodes rather than threading a prop
 *   through every (deeply nested, dynamic) menu item. React leaves these alone
 *   on re-render because the components always pass a constant `tabIndex` prop,
 *   so React's diff never re-touches the attribute we changed out-of-band.
 * - A MutationObserver re-syncs whenever items are added/removed (accordions
 *   expanding, async content), keeping exactly one tabbable item.
 * - Hidden items (collapsed accordions use `visibility: hidden`) are skipped via
 *   `checkVisibility`, falling back to `offsetParent` where unsupported.
 */

// Anything a keyboard user can land on inside the region. We intentionally do
// NOT filter by tabindex here: inactive items carry tabindex=-1 but must still
// be reachable via the arrow keys (programmatic focus works on tabindex=-1).
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), [role="button"]:not([aria-disabled="true"])';

// `queueMicrotask` is missing in some older runtimes (e.g. Safari < 12.1). Fall
// back to a resolved-Promise microtask so the MutationObserver still coalesces
// re-syncs everywhere the app otherwise runs, and a macrotask as a last resort.
const scheduleMicrotask = (cb: () => void): void => {
	if (typeof queueMicrotask === 'function') {
		queueMicrotask(cb);
	} else if (typeof Promise !== 'undefined') {
		void Promise.resolve().then(cb);
	} else {
		setTimeout(cb, 0);
	}
};

const isVisible = (el: HTMLElement): boolean => {
	const anyEl = el as HTMLElement & {
		checkVisibility?: (options?: Record<string, boolean>) => boolean;
	};
	if (typeof anyEl.checkVisibility === 'function') {
		// `visibilityProperty`/`checkVisibilityCSS` both requested for cross-version
		// coverage so collapsed accordions (visibility: hidden) are excluded.
		return anyEl.checkVisibility({ visibilityProperty: true, checkVisibilityCSS: true, opacityProperty: false });
	}
	// Fallback for browsers without checkVisibility() (Firefox/Safari). Exclude
	// elements hidden via `display: none` or `visibility: hidden` (the latter is
	// what MUI Collapse/Accordion applies when collapsed and is inherited by the
	// nav items inside), and treat zero-box (collapsed/height:0) content as hidden.
	const style = window.getComputedStyle(el);
	if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') {
		return false;
	}
	return el.getClientRects().length > 0;
};

export function useRovingTabIndex<T extends HTMLElement = HTMLDivElement>() {
	const containerRef = useRef<T | null>(null);
	const observerRef = useRef<MutationObserver | null>(null);

	const getItems = useCallback((): HTMLElement[] => {
		const root = containerRef.current;
		if (!root) return [];
		return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(isVisible);
	}, []);

	// Every focusable in the container, INCLUDING ones hidden inside collapsed
	// accordions. Used by syncTabIndex to clear stale tabindex on items that are
	// no longer visible (the visible-only `getItems` can't reach them).
	const getAllFocusables = useCallback((): HTMLElement[] => {
		const root = containerRef.current;
		if (!root) return [];
		return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
	}, []);

	/** Ensure exactly one visible item is tabbable; prefer `preferred` if given. */
	const syncTabIndex = useCallback(
		(preferred?: HTMLElement | null) => {
			// Reset EVERY focusable to -1 first — including items hidden inside a
			// collapsed accordion. Otherwise a hidden item keeps its default
			// tabindex=0 and stays reachable via Tab even though it isn't visible.
			const all = getAllFocusables();
			const items = all.filter(isVisible);
			if (items.length === 0) {
				all.forEach((el) => {
					if (el.tabIndex !== -1) el.tabIndex = -1;
				});
				return;
			}
			let active: HTMLElement | null = preferred && items.includes(preferred) ? preferred : null;
			if (!active) active = items.find((el) => el.tabIndex === 0) ?? null;
			if (!active)
				active = items.find((el) => el.matches('.Mui-selected, .active, [aria-current="page"]')) ?? null;
			if (!active) active = items[0];
			// Promote exactly one VISIBLE item; demote everything else (visible or
			// hidden) so there is always a single Tab stop into the region.
			all.forEach((el) => {
				const want = el === active ? 0 : -1;
				// Only write on a real change so the MutationObserver can't loop.
				if (el.tabIndex !== want) el.tabIndex = want;
			});
		},
		[getAllFocusables]
	);

	/**
	 * Callback ref: wires up the observer + initial sync exactly when the
	 * container node mounts. A plain `useEffect` would fire too early — the
	 * sidebar renders a loading skeleton first (no container), and the effect
	 * deps wouldn't change once the real nav swapped in, so setup would be missed.
	 */
	const setContainer = useCallback(
		(node: T | null) => {
			observerRef.current?.disconnect();
			observerRef.current = null;
			containerRef.current = node;
			if (!node) return;

			syncTabIndex();
			let scheduled = false;
			const observer = new MutationObserver(() => {
				if (scheduled) return;
				scheduled = true;
				scheduleMicrotask(() => {
					scheduled = false;
					syncTabIndex();
				});
			});
			observer.observe(node, { childList: true, subtree: true });
			observerRef.current = observer;
		},
		[syncTabIndex]
	);

	useEffect(() => () => observerRef.current?.disconnect(), []);

	const onKeyDown = useCallback(
		(event: KeyboardEvent<T>) => {
			if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
			const items = getItems();
			if (items.length === 0) return;
			const current = document.activeElement as HTMLElement | null;
			const index = current ? items.indexOf(current) : -1;
			let next: number;
			switch (event.key) {
				case 'ArrowDown':
					next = index < 0 ? 0 : (index + 1) % items.length;
					break;
				case 'ArrowUp':
					next = index < 0 ? items.length - 1 : (index - 1 + items.length) % items.length;
					break;
				case 'Home':
					next = 0;
					break;
				default: // End
					next = items.length - 1;
			}
			event.preventDefault();
			const target = items[next];
			syncTabIndex(target);
			target.focus();
		},
		[getItems, syncTabIndex]
	);

	const onFocus = useCallback(
		(event: FocusEvent<T>) => {
			const target = event.target as HTMLElement;
			if (target.matches(FOCUSABLE_SELECTOR)) syncTabIndex(target);
		},
		[syncTabIndex]
	);

	return {
		containerRef: setContainer,
		/** Spread onto the container element that wraps the focusable items. */
		rovingProps: { onKeyDown, onFocus }
	};
}

export default useRovingTabIndex;
